import React, { createContext, ReactNode, useContext, useMemo, useState } from "react";

export type RequestCallbackStatus = "" | "success" | "failed";

interface RequestCallbackData {
    status: RequestCallbackStatus;
    channel: string;
    phone: string;
    language: string;
    history: Partial<RequestCallbackData>[];
}

interface RequestCallbackContextType {
    dataMap: Record<string, RequestCallbackData>;
    updateRequestCallback: (sessionId: string, updates: Partial<Omit<RequestCallbackData, "history">>) => void;
    getRequestCallback: (sessionId: string) => RequestCallbackData | undefined;
}

const RequestCallbackContext = createContext<RequestCallbackContextType | undefined>(undefined);

interface RequestCallbackProviderProps {
    children: ReactNode;
}

export const RequestCallbackProvider: React.FC<RequestCallbackProviderProps> = ({ children }) => {
    const [dataMap, setDataMap] = useState<Record<string, RequestCallbackData>>({});

    const updateRequestCallback = (sessionId: string, updates: Partial<Omit<RequestCallbackData, "history">>) => {
        setDataMap(prev => {
            const existing = prev[sessionId] || {
                status: "",
                channel: "",
                phone: "",
                language: "",
                history: []
            };

            const updated = {
                ...existing,
                ...updates,
                history: [...existing.history, updates]
            };

            return {
                ...prev,
                [sessionId]: updated
            };
        });
    };

    const getRequestCallback = (sessionId: string) => {
        return dataMap[sessionId];
    };

    const contextValue = useMemo(() => ({
        dataMap,
        updateRequestCallback,
        getRequestCallback
    }), [dataMap]);

    return (
        <RequestCallbackContext.Provider value={contextValue}>
            {children}
        </RequestCallbackContext.Provider>
    );
};

export const useRequestCallback = (): RequestCallbackContextType => {
    const context = useContext(RequestCallbackContext);
    if (!context) {
        throw new Error("useRequestCallback must be used within a RequestCallbackProvider");
    }
    return context;
};